<template>
  <div>
    <SavedPaymentContainer
      v-if="!isShowEditForm"
      :is-select="card.is_select"
      :logo-src="logoSrc"
      :account-number="'**** **** **** ' + card.card_last_four"
      :is-clinic="isClinicCard"
      :expire-date="card.card_exp"
      :is-show-edit="isShowEdit"
      :is-show-delete="isShowDelete"
      :is-disabled="isDisabled"
      @edit="isShowEditForm = true"
      @remove="handleRemoveCard"
    />
    <div v-else class="edit-form-container pl-4">
      <div class="grid grid-cols-2 gap-4 w-2/3 mt-5">
        <div class="card-info-container col-span-2 flex items-center">
          <img :src="logoSrc" alt="card logo" class="logo-2 ml-3.5">
          <p class="ml-6 card">**** **** **** {{ card.card_last_four }}</p>
        </div>
        <el-input v-model="expDate" placeholder="MM/YYYY e.g. 12/2023" />
      </div>
      <div class="flex cursor-pointer mt-4" @click="isSetPrimary = !isSetPrimary">
        <span v-if="!isSetPrimary" class="material-icons-outlined check-box">
          check_box_outline_blank
        </span>
        <span v-else class="material-icons-outlined check-box">check_box</span>
        <p>Set as primary payment method</p>
      </div>
      <div class="my-7 flex gap-4">
        <NewLibraryButton color-type="dark-blue" @click="saveUpdate">
          Save
        </NewLibraryButton>
        <NewLibraryButton color-type="dark-blue-stroke" @click="isShowEditForm = false">
          Cancel
        </NewLibraryButton>
      </div>
    </div>
  </div>
</template>

<script>
import NewLibraryButton from "@/NewLibrary/Button.vue";
import SavedPaymentContainer from './SavedPaymentContainer.vue';
import { processCardLogo } from '../index.js';
import { notify } from '@/utils/notify';
import { deleteSavedPaymentApi, updateCreditCard} from '@/api/va-order-page/newChargingService';

export default {
  components: {
    SavedPaymentContainer,
    NewLibraryButton
  },
  props: {
    card: {
      type: Object,
      default: null,
    },
    isClinicCard: {
      type: Boolean,
      default: false,
    },
    isShowEdit: {
      type: Boolean,
      default: true
    },
    isShowDelete: {
      type: Boolean,
      default: true
    },
    accountId: {
      type: Number,
      default: null
    },
    accountRole: {
      type: String,
      default: "customer"
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isShowEditForm: false,
      expDate: this.card.card_exp.slice(0,2) + "/" + this.card.card_exp.slice(2),
      isSetPrimary: this.card.is_default === 1 ? true : false,
      currentYear: new Date().getFullYear().toString(),
      currentMonth: (new Date().getMonth() + 1).toString().length === 1
        ? "0" + (new Date().getMonth() + 1)
        : (new Date().getMonth() + 1).toString()
    }
  },

  computed: {
    logoSrc() {
      return require('@/assets/orderTest/' + processCardLogo(this.card.card_type, this.card.is_select));
    },
    cardExp() {
      return this.card.card_exp.slice(0,2) + "/" + this.card.card_exp.slice(2);
    }
  },

  watch: {
    expDate(newVal, oldVal) {
      if (newVal.length === 0)
        return;
      let curInput = newVal[newVal.length-1];
      const regex = /[0-9]/;
      // delete user current input if it is not a number (need deal with '/')
      if (!curInput.match(regex)) {
        if (curInput === "/") {
          if (newVal.length !== 3)
            this.expDate = this.expDate.slice(0, -1);
        }
        else
          this.expDate = this.expDate.slice(0, -1);
      }
      else {
        // autofill the "/" between month&year
      if (oldVal.length === 1 && newVal.length === 2)
        this.expDate += "/";
      }
    }
  },

  emits: ["removeCard", "updateCard"],

  methods: {
    async handleRemoveCard() {
      // delete selected card
      this.$emit("removeCard", this.card);
      try {
        await deleteSavedPaymentApi({
          paymentMethodId: this.card.id,
        })
        notify({ type: 'success', title: 'Success!', message: 'Successfully delete credit card.' })
      } catch(err) {
        console.log("delete card error", err);
      }

    },

    saveUpdate() {
      // check valid of exp date
      let [expMonth, expYear] = this.expDate.split("/");
      if (expYear < this.currentYear || (expMonth < this.currentMonth && expYear === this.currentYear)) {
        let message = "Invalid expire date.";
        notify({ type: 'error', message }); 
        return;
      }

      const expiry_date = `${expMonth}${expYear}`;
      updateCreditCard(this.card.id, { expiry_date })
        .then(() => {
          this.isShowEditForm = false;
          this.$emit("updateCard", {
            id: this.card.id,
            is_default: this.isSetPrimary ? 1 : 0,
            card_exp: this.expDate.split("/").join("")
          });
          notify({ type: 'success', title: 'Success!', message: 'Successfully update card.' });
        })
        .catch(err => {
          console.log("update card error", err);
          notify({ type: 'error', message: 'Failed to update card.' });
        });
    },
  },
};
</script>

<style scoped>
.card {
  height: 19px;
  color: rgba(0,0,0,1);
  font-family: Roboto;
  font-size: 16px;
  letter-spacing: 0.46px;
}

.edit-form-container {
  box-sizing: border-box;
  border: 1px solid #C9CACD;
  border-radius: 2px;
  width: 100%;
}

.logo-2 {
  width: 40px;
}

.card-info-container {
  box-sizing: border-box;
  border: 1px solid #C9CACD;
  border-radius: 2px;
}

.check-box {
  color: rgba(0,0,0,0.26);
  margin-right: 12px;
}

</style>